import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import { adminApi } from "../../lib/adminApi";
import { toast } from "sonner";
import { DollarSign, MousePointerClick, Eye, Link2, Unlink, ExternalLink, AlertCircle, CheckCircle2 } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line,
} from "recharts";

const TOOLTIP_STYLE = {
  background: "rgba(10,10,10,0.92)",
  border: "1px solid rgba(76,175,80,0.35)",
  borderRadius: 8,
  color: "#fff",
  fontSize: 12,
};

const ZONE_LABELS = {
  Z1_bottom_banner: "Z1 — Bottom Banner",
  Z2_in_feed_native: "Z2 — In-Feed Native",
  Z3_interstitial: "Z3 — Interstitial",
  Z4_sidebar: "Z4 — Sidebar (desktop)",
  Z5_pre_music_gate: "Z5 — Pre-Music Gate",
};

export default function AdsScreen() {
  const [config, setConfig] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, r, s] = await Promise.all([
        adminApi.get("/ads/config"),
        adminApi.get("/ads/revenue"),
        adminApi.get("/ads/oauth/status"),
      ]);
      setConfig(c.data); setRevenue(r.data); setStatus(s.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const toggle = (zone) => {
    if (!config) return;
    setConfig({ ...config, zones: { ...config.zones, [zone]: !config.zones[zone] } });
  };

  const saveZones = async () => {
    try { await adminApi.put("/ads/config", { enabled: config.enabled, zones: config.zones }); toast.success("Zone config saved"); }
    catch { toast.error("Save failed"); }
  };

  const connectGoogle = async () => {
    setBusy(true);
    try {
      const { data } = await adminApi.get("/ads/oauth/login-url");
      window.open(data.url, "_blank", "width=520,height=680");
      // Poll status every 2s up to 90s
      const start = Date.now();
      const poll = setInterval(async () => {
        try {
          const { data: s } = await adminApi.get("/ads/oauth/status");
          if (s.connected) {
            clearInterval(poll); setStatus(s); toast.success("Connected to Google Ads");
            load();
          } else if (Date.now() - start > 90_000) {
            clearInterval(poll);
          }
        } catch {}
      }, 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Connection failed — check Google Cloud setup");
    } finally { setBusy(false); }
  };

  const disconnectGoogle = async () => {
    if (!window.confirm("Disconnect Google Ads? You'll need to reconnect for live data.")) return;
    try { await adminApi.post("/ads/oauth/disconnect", {}); toast.success("Disconnected"); load(); }
    catch { toast.error("Failed"); }
  };

  if (!config || !revenue || !status) {
    return <AdminLayout><div className="p-10 text-center"><span className="inline-block w-3 h-3 rounded-full bg-[#4CAF50] dot-pulse" /></div></AdminLayout>;
  }

  // Build a daily revenue line series
  const byDate = {};
  for (const r of [...(revenue.adsense?.rows || []), ...(revenue.admob?.rows || [])]) {
    if (!r.date) continue;
    if (!byDate[r.date]) byDate[r.date] = { date: r.date, adsense: 0, admob: 0 };
    byDate[r.date][r.source] += r.earnings;
  }
  const dailySeries = Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));

  return (
    <AdminLayout>
      <h1 className="text-2xl font-bold text-shadow-green mb-1">Ads & Revenue</h1>
      <p className="text-muted-soft text-sm mb-6">Live AdSense + AdMob data via Google Publisher APIs.</p>

      {/* Google connection card */}
      <GoogleConnectCard status={status} onConnect={connectGoogle} onDisconnect={disconnectGoogle} busy={busy} live={revenue.live} />

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-3"><span className="text-[10px] uppercase tracking-wider text-muted-soft">Impressions</span><Eye size={14} className="text-[#4CAF50]" /></div>
          <p className="text-2xl font-extrabold">{(revenue.impressions || 0).toLocaleString()}</p>
        </div>
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-3"><span className="text-[10px] uppercase tracking-wider text-muted-soft">Clicks</span><MousePointerClick size={14} className="text-[#FF8F00]" /></div>
          <p className="text-2xl font-extrabold">{(revenue.clicks || 0).toLocaleString()}</p>
        </div>
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-3"><span className="text-[10px] uppercase tracking-wider text-muted-soft">CTR</span></div>
          <p className="text-2xl font-extrabold">{revenue.ctr}%</p>
        </div>
        <div className="glass-card p-5">
          <div className="flex items-center justify-between mb-3"><span className="text-[10px] uppercase tracking-wider text-muted-soft">Est. revenue</span><DollarSign size={14} className="text-[#FFD54F]" /></div>
          <p className="text-2xl font-extrabold text-[#FFD54F]">${(revenue.estimated_revenue_usd || 0).toLocaleString()}</p>
        </div>
      </div>

      {/* AdSense + AdMob breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <SourceCard label="AdSense (web)" data={revenue.adsense?.summary || {}} color="#4CAF50" pubId={status.adsense_publisher_id} />
        <SourceCard label="AdMob (mobile)" data={revenue.admob?.summary || {}} color="#7B1FA2" pubId={status.admob_publisher_id} />
      </div>

      {/* Daily revenue chart */}
      <div className="glass-card p-5 mb-6">
        <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-3">Daily revenue (last 7 days)</p>
        <div style={{ height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailySeries}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.5)" }} />
              <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} tickFormatter={(v) => `$${v}`} />
              <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => `$${v.toFixed(2)}`} />
              <Line type="monotone" dataKey="adsense" stroke="#4CAF50" strokeWidth={2.2} dot={{ r: 3 }} name="AdSense" />
              <Line type="monotone" dataKey="admob" stroke="#7B1FA2" strokeWidth={2.2} dot={{ r: 3 }} name="AdMob" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-card p-5">
          <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-3">Revenue by unit</p>
          <div style={{ height: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenue.by_zone || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="zone" tick={{ fontSize: 10, fill: "rgba(255,255,255,0.5)" }} angle={-20} textAnchor="end" height={60} />
                <YAxis tick={{ fontSize: 11, fill: "rgba(255,255,255,0.5)" }} />
                <Tooltip contentStyle={TOOLTIP_STYLE} formatter={(v) => `$${Number(v).toFixed(2)}`} />
                <Bar dataKey="revenue" fill="#FFD54F" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="glass-card p-5">
          <p className="text-[11px] uppercase tracking-wider text-muted-soft mb-3">Zone toggles</p>
          <div className="space-y-2 mb-4">
            {Object.keys(ZONE_LABELS).map((z) => (
              <div key={z} className="flex items-center justify-between p-3 rounded-lg" style={{ background: "rgba(255,255,255,0.02)" }} data-testid={`zone-row-${z}`}>
                <div>
                  <p className="text-sm font-semibold">{ZONE_LABELS[z]}</p>
                  <p className="text-[11px] text-muted-soft">
                    {(revenue.by_zone || []).find((b) => b.zone === z)?.impressions?.toLocaleString() || 0} impressions
                  </p>
                </div>
                <button
                  onClick={() => toggle(z)}
                  className={`relative w-11 h-6 rounded-full ${config.zones[z] ? "bg-[#4CAF50]" : "bg-white/15"}`}
                  style={config.zones[z] ? { boxShadow: "var(--glow-green)" } : {}}
                  data-testid={`toggle-${z}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-transform ${config.zones[z] ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
            ))}
          </div>
          <button onClick={saveZones} className="btn-primary w-full" data-testid="save-zones">Save zone config</button>
        </div>
      </div>
    </AdminLayout>
  );
}

function SourceCard({ label, data, color, pubId }) {
  return (
    <div className="glass-card p-5" style={{ borderColor: `${color}55` }} data-testid={`source-${label.toLowerCase().split(' ')[0]}`}>
      <div className="flex items-center justify-between mb-3">
        <p className="text-[11px] uppercase tracking-wider text-muted-soft">{label}</p>
        <span className="text-[10px] text-muted-soft">{pubId || "—"}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-2">
        <Metric label="Impressions" value={(data.impressions || 0).toLocaleString()} />
        <Metric label="Clicks" value={(data.clicks || 0).toLocaleString()} />
        <Metric label="CTR" value={`${((data.ctr || 0) * 100).toFixed(2)}%`} />
        <Metric label="Earnings" value={`$${(data.earnings || 0).toFixed(2)}`} accent={color} />
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-muted-soft">{label}</p>
      <p className="text-base font-extrabold mt-0.5" style={accent ? { color: accent, textShadow: `0 0 10px ${accent}55` } : {}}>{value}</p>
    </div>
  );
}

function GoogleConnectCard({ status, onConnect, onDisconnect, busy, live }) {
  const configured = status.configured;
  const connected = status.connected;
  return (
    <div
      className="glass-card-strong p-5 mb-6 flex items-start gap-4 flex-wrap"
      style={{
        borderColor: connected ? "rgba(76,175,80,0.5)" : "rgba(255,143,0,0.5)",
        boxShadow: connected ? "var(--glow-green)" : "var(--glow-amber)",
      }}
      data-testid="google-connect-card"
    >
      <span className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
            style={{ background: connected ? "rgba(76,175,80,0.18)" : "rgba(255,143,0,0.18)" }}>
        {connected ? <CheckCircle2 size={22} className="text-[#4CAF50]" /> : <AlertCircle size={22} className="text-[#FF8F00]" />}
      </span>
      <div className="flex-1 min-w-[260px]">
        <p className="text-[10px] uppercase tracking-wider text-muted-soft">Google Publisher Integration</p>
        <h3 className="text-lg font-bold mt-0.5">
          {connected ? "Connected" : configured ? "Ready to connect" : "Setup required"}
        </h3>
        {connected && (
          <p className="text-sm text-soft mt-1">
            Live AdSense + AdMob data is flowing.
            <span className="text-muted-soft"> Linked: {status.connected_email || "—"}</span>
          </p>
        )}
        {!connected && configured && (
          <p className="text-sm text-soft mt-1">
            OAuth credentials configured. Click <strong>Connect Google</strong> and authorise with the publisher account.
          </p>
        )}
        {!configured && (
          <div className="text-sm text-soft mt-2">
            <p>To enable live data, set in <code className="bg-black/40 px-1.5 py-0.5 rounded text-[#4CAF50]">backend/.env</code>:</p>
            <ul className="text-[12px] text-muted-soft mt-2 space-y-0.5 ml-4">
              <li>• <code>GOOGLE_ADS_CLIENT_ID</code> {status.client_id_set ? "✓" : "✗ missing"}</li>
              <li>• <code>GOOGLE_ADS_CLIENT_SECRET</code> {status.client_secret_set ? "✓" : "✗ missing"}</li>
            </ul>
            <p className="text-xs text-muted-soft mt-2">
              Authorize redirect URI in Google Cloud Console:{" "}
              <code className="bg-black/40 px-1.5 py-0.5 rounded text-[10px]">{status.redirect_uri}</code>
            </p>
            <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#4CAF50] text-sm mt-2 hover:underline">
              Open Google Cloud Credentials <ExternalLink size={12} />
            </a>
          </div>
        )}
        {!live && connected && (
          <p className="text-[11px] text-[#FF8F00] mt-2">Currently showing cached/last-known data.</p>
        )}
      </div>
      <div className="flex flex-col gap-2 flex-shrink-0">
        {connected ? (
          <button onClick={onDisconnect} className="btn-outline inline-flex items-center gap-2 text-sm" data-testid="ads-disconnect">
            <Unlink size={14} /> Disconnect
          </button>
        ) : (
          <button onClick={onConnect} disabled={busy || !configured} className="btn-primary inline-flex items-center gap-2 text-sm" data-testid="ads-connect">
            <Link2 size={14} /> {busy ? "Opening…" : "Connect Google"}
          </button>
        )}
        <span className={`text-[10px] text-center font-semibold ${live ? "text-[#4CAF50]" : "text-muted-soft"}`}>
          {live ? "● LIVE" : "● MOCK DATA"}
        </span>
      </div>
    </div>
  );
}
